import {
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import {
  Auth,
  authState,
  getRedirectResult,
  GoogleAuthProvider,
  idToken,
  isSignInWithEmailLink,
  OAuthProvider,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User as FirebaseUser,
  user,
} from "@angular/fire/auth";
import { UserService } from "../users/users.service";
import { User } from "../users/users.interface";
import { Store } from "@ngrx/store";
import {
  ChangeAccountAction,
  ResetAccountAction,
} from "../accounts/accounts.actions";
import { CURRENT_ACCOUNT_LOCAL_STORAGE_KEY } from "../accounts/accounts.interface";
import { ToastrService } from "../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { getCurrentAccount } from "src/app/accounts/accounts.reducer";
import { Observable, take } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

const UTM_KEY = "utm";
const RETURN_URL_KEY = "returnUrl";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private injector = inject(Injector);
  private sessionStart: Date | null = null;
  private activeDuration = 0;
  private sessionActiveStart: Date | null = null;
  private idleTimer: number | null = null;
  currentDomain: string | null = null;
  user$ = user(this.auth);
  idToken$ = idToken(this.auth);
  idToken;
  authState$ = authState(this.auth);
  authState;
  appUser: User | undefined;
  hasCompanyManagerAccess = false;
  canChangeAccounts = false;
  isSuperAdmin = false;
  isCompanyManager = false;
  isGroupManager = false;
  isReseller = false;
  isGoogleAdmin = false;
  trialFinishDate;
  lastGoogleSync;
  dataSource;
  hasEmployee = false;

  constructor(
    public router: Router,
    private http: HttpClient,
    private userService: UserService,
    private store: Store,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {
    this.idToken$.subscribe((token: string | null) => {
      this.idToken = token;
    });
    this.authState$.subscribe((aUser: FirebaseUser | null) => {
      this.authState = aUser;
    });
    this.store.select(getCurrentAccount).subscribe((domain) => {
      this.currentDomain = domain;
    });
    this.storeUTMParams();

    this.sessionStart = new Date();
    this.sessionActiveStart = new Date();
    this.setupActivityListeners();

    window.addEventListener("beforeunload", () => this.reportSession());
  }

  private setupActivityListeners(): void {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((event) =>
      window.addEventListener(event, () => this.resetIdleTimer()),
    );

    this.resetIdleTimer();
  }

  private resetIdleTimer(): void {
    if (!this.sessionActiveStart) {
      this.sessionActiveStart = new Date();
    }

    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = window.setTimeout(() => {
      if (this.sessionActiveStart) {
        const now = new Date();
        this.activeDuration +=
          now.getTime() - this.sessionActiveStart.getTime();
        this.sessionActiveStart = null;
      }
    }, 30000);
  }

  reportSession(): void {
    if (!this.sessionStart) {
      return;
    }
    if (!this.appUser?.email) {
      return;
    }
    if (this.sessionActiveStart) {
      const now = new Date();
      this.activeDuration += now.getTime() - this.sessionActiveStart.getTime();
      this.sessionActiveStart = null;
    }
    const domain = this.currentDomain || this.appUser.domain;
    if (!domain) {
      this.sessionStart = null;
      return;
    }
    const sessionEnd = new Date();
    const payload = {
      email: this.appUser.email,
      domain,
      clientId: this.appUser.clientId,
      sessionStart: this.sessionStart.toISOString(),
      sessionEnd: sessionEnd.toISOString(),
      activeDuration: Math.floor(this.activeDuration / 1000),
    };

    const url = `${environment.apiUrl}/user-activity/report`;
    const blob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });

    navigator.sendBeacon(url, blob);

    this.sessionStart = null;
  }

  async sendSignWithEmail(email: string, lang: string): Promise<void> {
    try {
      const utm = this.getUTMParams();
      const response = await this.http
        .post<{ userExists: boolean; noPermissionsForDomain?: boolean }>(
          "/auth/sign-with-email",
          {
            email,
            lang,
            utm,
          },
        )
        .toPromise();

      if (!response.userExists) {
        if (response.noPermissionsForDomain) {
          this.toastr.warning(
            this.translate.instant("login.noPermissionsForDomain") as string,
            "",
            { timeOut: 5000 },
          );
        } else {
          this.toastr.info(
            this.translate.instant("login.userNotFound") as string,
            "",
            { timeOut: 3000 },
          );
          setTimeout(() => {
            window.location.href = "https://gsignature.com/sign-up";
          }, 3000);
        }
      } else {
        this.toastr.success(
          this.translate.instant("login.emailSentSuccess") as string,
        );
      }
    } catch (error) {
      console.error("Error sending email link:", error);
      this.toastr.error(
        this.translate.instant("login.emailSentError") as string,
      );
      throw error;
    }
  }

  public async loginWithEmailLink(): Promise<boolean> {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    const isEmailLink = runInInjectionContext(this.injector, () =>
      isSignInWithEmailLink(this.auth, window.location.href),
    );
    if (email && isEmailLink) {
      try {
        await runInInjectionContext(this.injector, () =>
          signInWithEmailLink(this.auth, email, window.location.href),
        );
        return true;
      } catch (error) {
        console.error("Error signing in with email link:", error);
        return false;
      }
    }
    return false;
  }

  public async loginWithGoogle() {
    if (window.location.hostname === "localhost") {
      await signInWithPopup(this.auth, new GoogleAuthProvider());
      await this.router.navigate(["/login/callback"]);
    } else {
      window.history.replaceState({}, "", "/login/callback");
      await signInWithRedirect(this.auth, new GoogleAuthProvider());
    }
  }

  public async loginWithMicrosoft() {
    const provider = new OAuthProvider("microsoft.com");
    provider.addScope("email");
    provider.addScope("profile");

    provider.setCustomParameters({
      prompt: "select_account",
    });

    if (window.location.hostname === "localhost") {
      await signInWithPopup(this.auth, provider);
      await this.router.navigate(["/login/callback"]);
    } else {
      window.history.replaceState({}, "", "/login/callback");
      await signInWithRedirect(this.auth, provider);
    }
  }

  public async issueExtensionCustomToken(): Promise<{
    customToken: string;
    email: string;
  } | null> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser?.email) {
      return null;
    }

    this.idToken = await firebaseUser.getIdToken(true);
    const response = await this.http
      .post<{ customToken: string; email: string }>(
        "/auth/extension-custom-token",
        {},
      )
      .toPromise();
    if (!response?.customToken) {
      return null;
    }
    return {
      customToken: response.customToken,
      email: (response.email || firebaseUser.email).toLowerCase(),
    };
  }

  public async loginWithPassword(
    email: string,
    password: string,
  ): Promise<{ customToken: string; email: string }> {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      throw new Error("invalid_credentials");
    }
    const response = await this.http
      .post<{
        customToken?: string;
        email?: string;
      }>("/auth/password-login", {
        email: normalizedEmail,
        password,
        issueCustomToken: true,
      })
      .toPromise();
    if (!response?.customToken || !response.email) {
      throw new Error("invalid_credentials");
    }
    return {
      customToken: response.customToken,
      email: response.email.trim().toLowerCase(),
    };
  }

  public isLoginWithLink(): boolean {
    return runInInjectionContext(this.injector, () =>
      isSignInWithEmailLink(this.auth, window.location.href),
    );
  }

  async logOut(): Promise<void> {
    this.reportSession();
    await signOut(this.auth);
    this.clearSessionState();
    this.store.dispatch(new ResetAccountAction());
    await this.router.navigate(["login"]);
  }

  private clearSessionState(): void {
    this.sessionStart = null;
    this.sessionActiveStart = null;
    this.activeDuration = 0;
    this.currentDomain = null;
    this.clearAppUser();
    window.localStorage.removeItem(CURRENT_ACCOUNT_LOCAL_STORAGE_KEY);
    window.localStorage.removeItem(RETURN_URL_KEY);
    window.sessionStorage.clear();
  }

  getToken() {
    return this.idToken$;
  }

  getAppUser() {
    return this.appUser;
  }

  public clearAppUser() {
    this.appUser = null;
  }

  async getRedirectResult(): Promise<
    | { continue: true }
    | {
        continue: false;
        reason: "no_permissions" | "not_found" | "requires_onboarding";
        domain?: string;
      }
  > {
    try {
      const result = await runInInjectionContext(this.injector, () =>
        getRedirectResult(this.auth),
      );
      const firebaseUser = result?.user ?? this.auth.currentUser;
      if (!firebaseUser?.email) {
        await this.router.navigate(["/login"]);
        return { continue: false, reason: "not_found" };
      }

      const email = firebaseUser.email;
      const utm = this.getUTMParams();
      const lang = this.translate.currentLang || "en";
      const providerId = firebaseUser.providerData?.[0]?.providerId ?? "";
      const isMicrosoft = providerId === "microsoft.com";
      const endpoint = isMicrosoft
        ? "/auth/sign-with-microsoft"
        : "/auth/sign-with-google";
      const response = await this.http
        .post<{
          userExists: boolean;
          noPermissionsForDomain?: boolean;
          requiresOnboarding?: boolean;
          domain?: string;
        }>(endpoint, {
          email,
          lang,
          utm,
        })
        .toPromise();

      if (
        response.userExists &&
        response.requiresOnboarding &&
        response.domain
      ) {
        return {
          continue: false,
          reason: "requires_onboarding",
          domain: response.domain,
        };
      }
      if (response.userExists) {
        return { continue: true };
      }
      if (response.noPermissionsForDomain) {
        this.toastr.warning(
          this.translate.instant("login.noPermissionsForDomain") as string,
          "",
          { timeOut: 5000 },
        );
        await signOut(this.auth);
        await this.router.navigate(["/login"]);
        return { continue: false, reason: "no_permissions" };
      }
      this.toastr.info(
        this.translate.instant("login.userNotFound") as string,
        "",
        { timeOut: 3000 },
      );
      await signOut(this.auth);
      setTimeout(() => {
        window.location.href = "https://gsignature.com/sign-up";
      }, 3000);
      return { continue: false, reason: "not_found" };
    } catch (error) {
      console.error("SSO login validation error:", error);
      await signOut(this.auth);
      await this.router.navigate(["/login"]);
      return { continue: false, reason: "not_found" };
    }
  }

  getGeneratorToken(userData: User): Observable<string> {
    const payload = {
      email: userData.email,
      domain: this.currentDomain,
    };

    return this.http
      .post<{ token: string }>("/generator/generate-token", payload)
      .pipe(map((response) => response.token));
  }

  private storeUTMParams(): void {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    params.forEach((value, key) => {
      if (key.startsWith("utm_") || key === "gad_source" || key === "gclid") {
        utm[key] = value;
      }
    });
    if (Object.keys(utm).length > 0) {
      localStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  }

  private getUTMParams(): Record<string, string> {
    const storedUTM = localStorage.getItem(UTM_KEY);
    if (storedUTM) {
      return JSON.parse(storedUTM) as Record<string, string>;
    }
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    params.forEach((value, key) => {
      if (key.startsWith("utm_") || key === "gad_source" || key === "gclid") {
        utm[key] = value;
      }
    });
    return utm;
  }

  fetchCurrent = () =>
    this.userService
      .current()
      .toPromise()
      .then((currentUser: User) => {
        this.store
          .select(getCurrentAccount)
          .pipe(take(1))
          .subscribe((currentAccount) => {
            if (
              !currentAccount ||
              !currentUser.availableDomains.includes(currentAccount)
            ) {
              this.store.dispatch(
                new ChangeAccountAction(currentUser.availableDomains[0]),
              );
            }
          });

        this.appUser = currentUser;
        this.hasCompanyManagerAccess = [
          "SUPER_ADMIN",
          "RESELLER",
          "GROUP_MANAGER",
          "COMPANY_MANAGER",
        ].includes(currentUser.role);
        this.canChangeAccounts =
          currentUser.availableDomains.length > 1 ||
          currentUser.hasPartnerAccount;
        this.isSuperAdmin = currentUser.role === "SUPER_ADMIN";
        this.isReseller = currentUser.role === "RESELLER";
        this.isGroupManager = currentUser.role === "GROUP_MANAGER";
        this.isCompanyManager = currentUser.role === "COMPANY_MANAGER";
        this.trialFinishDate = currentUser.trialFinishDate;
        this.isGoogleAdmin = currentUser.isGoogleAdmin;
        this.lastGoogleSync = currentUser.lastGoogleSync;
        this.dataSource = currentUser.dataSource;
        this.hasEmployee = currentUser.hasEmployee;
      })
      .then(() =>
        this.appUser.planId || this.appUser.isPremium
          ? true
          : this.router.navigate(["/plan"]),
      )
      .catch(() => this.logOut().then(() => false));

  waitForAuthReady(): Promise<void> {
    return runInInjectionContext(this.injector, () =>
      this.auth.authStateReady(),
    );
  }

  public changeAvatarUrl = (avatarUrl: string): void => {
    if (this.appUser) {
      this.appUser.thumbnailPhotoUrl = avatarUrl;
    }
  };

  getDefaultAppCommands(): string[] {
    if (this.canChangeAccounts) {
      return ["/accounts"];
    }
    if (!this.hasCompanyManagerAccess) {
      return ["/my-account"];
    }
    return ["/signatures", "custom"];
  }
}
